import { useCallback, useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { UploadFile } from "antd";
import { AxiosError } from "axios";

import { IAward, IFederalLottery, IGiveaway } from "../../../../../interfaces";

import { EmitError } from "../../../../../utils/EmitError";
import GiveawaysService from "../../../../../services/http/services/GiveawaysService";
import { EmitSuccessAlert } from "../../../../../utils/EmitSuccessAlert";
import LotteryFederalService from "../../../../../services/http/services/LotteryFederalService";
import { EGiveawayStatus } from "../../../../../enums";

const ACCEPTED_FILE_TYPES = [
    'application/json',
]

export interface IChangeFileProps {
    onChangeHookForm: (event: any) => void,
    file: UploadFile,
}

interface IInputs {
    participants_file: UploadFile;
    hash: string;
}

interface IProps {
    giveaway: IGiveaway | undefined;
    refreshGiveaway: () => void;
}

export default function useProcessData({ giveaway, refreshGiveaway }: IProps) {
    const [isSubmiting, setIsSubmiting] = useState<boolean>(false);
    const [isDownloadingFile, setIsDownloadingFile] = useState<boolean>(false);
    const [hash, setHash] = useState<string | undefined | null>(giveaway?.hash_with_participants);
    const [file, setFile] = useState<string | undefined | null>(giveaway?.file_with_participants);
    const [federalLottery, setFederalLottery] = useState<IFederalLottery | undefined>();
    const [processedData, setProcessedData] = useState<boolean>(giveaway?.status !== EGiveawayStatus.WAITING_RAFFLE);
    const [beingUploadedParticipantsFileName, setBeingUploadedParticipantsFileName] = useState<string | undefined>();
    const [selectedAward, setSelectedAward] = useState<IAward | undefined>();
    const [modalAwardVisible, setModalAwardVisible] = useState<boolean>(false);

    const {
        control,
        handleSubmit,
        formState: { errors },
        setError,
        clearErrors,
        watch
    } = useForm<IInputs>({
        shouldFocusError: false,
        defaultValues: {
            participants_file: undefined,
            hash: giveaway?.hash_with_participants || '',
        }
    });

    const onClickViewWinner = useCallback((award: IAward) => {
        setSelectedAward(award);
        setModalAwardVisible(true);
    }, []);

    const handleCloseModalAwardWinner = useCallback(() => {
        setModalAwardVisible(false);
    }, []);

    const handleSubmitForm: SubmitHandler<IInputs> = useCallback(async (data) => {
        try {

            if (!giveaway) {
                return;
            }

            setIsSubmiting(true);
            const formData = new FormData();

            if (data.participants_file?.originFileObj) {
                formData.append('file', data.participants_file.originFileObj);
            }

            await GiveawaysService.uploadGiveawayFederalLotteryResult({raffleId: giveaway.id, formData});


            setProcessedData(true);
            setHash(hash);
            setFile(file);
            setIsSubmiting(false);
            refreshGiveaway();
            EmitSuccessAlert('Resultado processado com sucesso!');

        } catch (err) {
            EmitError(err);
            setIsSubmiting(false);
        }
    }, [giveaway, refreshGiveaway, file, hash]);

    const dummyRequest = useCallback(({ onSuccess }: any) => {
        setTimeout(() => {
            onSuccess("ok");
        }, 0);
    }, []);

    const handleChangeParticipantsFile = useCallback(({ onChangeHookForm, file }: IChangeFileProps) => {
        if (file?.type && !ACCEPTED_FILE_TYPES.includes(file.type.toLowerCase())) {
            setError('participants_file', { message: 'Formato de arquivo inválido! O formato aceito é apenas JSON' });
            return;
        }

        clearErrors('participants_file');
        onChangeHookForm(file);
        setBeingUploadedParticipantsFileName(file.name);
    }, [setError, clearErrors]);

    const handleClickDownloadFile = useCallback(async () => {
        if (!file) {
            return;
        }

        const controller = new AbortController();

        try {
            setIsDownloadingFile(true);
            const responseFile = await GiveawaysService.downloadGiveawayTicketsFile({fileName: file, signal: controller.signal});

            const link = document.createElement('a');
            link.href = window.URL.createObjectURL(responseFile);
            link.download = file;
            link.click();
            window.URL.revokeObjectURL(link.href);
            setIsDownloadingFile(false);
        } catch (err) {
            if (err instanceof AxiosError && err.name === 'CanceledError') {
                return;
            }

            setIsDownloadingFile(false);
            EmitError(err, true, 'holdGiveawayExportData_ajksdkasdjasjdasd')
        }

        return () => {
            controller.abort();
        }
    }, [file]);

        useEffect(() => {
        const controller = new AbortController();

        async function loadFederalLottery() {
            try {
                if (giveaway?.date) {
                    const federalLottery = await LotteryFederalService.getFederalLotteryByDate(giveaway.date, controller.signal);
                    setFederalLottery(federalLottery);
                }
            } catch (err) {
                if (err instanceof AxiosError && err.name === 'CanceledError') {
                    return;
                }

                EmitError(err);
            }
        }

        loadFederalLottery();

        return () => {
            controller.abort();
        }
    }, [giveaway]);

    return {
        control,
        handleSubmit,
        handleSubmitForm,
        errors,
        dummyRequest,
        handleChangeParticipantsFile,
        beingUploadedParticipantsFileName,
        handleClickDownloadFile,
        file,
        hash,
        isDownloadingFile,
        isSubmiting,
        ACCEPTED_FILE_TYPES,
        federalLottery,
        processedData,
        watch,
        selectedAward,
        modalAwardVisible,
        handleCloseModalAwardWinner,
        onClickViewWinner
    }
}
